/**
 * Component Layout Configuration Types (SoW-311)
 *
 * Defines layout customization options for component boxes in the Block Diagram.
 */
/** Icon size to pixel mapping */
export const ICON_SIZE_PX = {
    xs: 16,
    sm: 20,
    md: 24,
    lg: 32,
    xl: 48,
    xxl: 64,
    '3xl': 80,
    '4xl': 96,
    '5xl': 128,
};
/** Font size to pixel mapping */
export const TEXT_FONT_SIZE_PX = {
    xs: 10,
    sm: 12,
    md: 14,
    lg: 16,
    xl: 20,
    '2xl': 24,
    '3xl': 30,
    '4xl': 36,
    '5xl': 48,
};
/**
 * Default layout configuration applied when layoutConfig is null
 */
export const DEFAULT_LAYOUT_CONFIG = {
    icon: {
        size: 'md',
        position: 'top-center',
        offsetX: 0,
        offsetY: 0,
    },
    text: {
        position: 'bottom',
        alignment: 'center',
        fontSize: 'sm',
        wrap: true,
        maxLines: 2,
    },
};
/**
 * All 9 icon positions for UI rendering
 */
export const ICON_POSITIONS = [
    'top-left',
    'top-center',
    'top-right',
    'center-left',
    'center',
    'center-right',
    'bottom-left',
    'bottom-center',
    'bottom-right',
];
/**
 * All icon sizes for UI rendering
 */
export const ICON_SIZES = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl', '3xl', '4xl', '5xl'];
/**
 * All text positions for UI rendering
 */
export const TEXT_POSITIONS = ['top', 'bottom', 'inside-top', 'inside-bottom', 'hidden'];
/**
 * All text alignments for UI rendering
 */
export const TEXT_ALIGNMENTS = ['left', 'center', 'right'];
/**
 * All text font sizes for UI rendering
 */
export const TEXT_FONT_SIZES = ['xs', 'sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl'];
/**
 * Get effective icon size in pixels (custom overrides preset)
 */
export function getIconSizePx(config) {
    return config.customSizePx ?? ICON_SIZE_PX[config.size];
}
/**
 * Get effective font size in pixels (custom overrides preset)
 */
export function getFontSizePx(config) {
    return config.customFontSizePx ?? TEXT_FONT_SIZE_PX[config.fontSize];
}
/**
 * Size preset dimensions
 */
export const PARENT_SIZE_PRESETS = {
    S: { width: 80, height: 60 },
    M: { width: 120, height: 80 },
    L: { width: 180, height: 120 },
};
/**
 * Default collapsed layout settings
 */
export const DEFAULT_COLLAPSED_LAYOUT = {
    width: 120,
    height: 80,
    iconPosition: 'center',
    iconSize: 'md',
    textSize: 12,
    iconOnly: false,
    showCount: true,
    showExpand: true,
    displayMode: 'bar', // FEA-046: Default to header bar mode
    titlePosition: 'below', // Default: title below icon in centered mode
};
/**
 * Default expanded layout settings
 */
export const DEFAULT_EXPANDED_LAYOUT = {
    width: 300,
    height: 200,
    headerHeight: 40,
    headerIcon: 'left',
    headerMode: 'bar', // SoW-443: Default to header bar mode
    textSize: 14,
    childSpacing: 16,
    childPadding: 16,
    autoFit: true,
};
/**
 * Default parent layout configuration
 */
export const DEFAULT_PARENT_LAYOUT = {
    collapsed: DEFAULT_COLLAPSED_LAYOUT,
    expanded: DEFAULT_EXPANDED_LAYOUT,
};
/**
 * Minimum dimensions for collapsed parent
 */
export const MIN_COLLAPSED_SIZE = { width: 60, height: 40 };
/**
 * Maximum dimensions for collapsed parent
 */
export const MAX_COLLAPSED_SIZE = { width: 1200, height: 800 };
/**
 * Minimum dimensions for expanded parent
 */
export const MIN_EXPANDED_SIZE = { width: 150, height: 100 };
/**
 * Get effective icon size for collapsed layout in pixels
 */
export function getCollapsedIconSizePx(settings) {
    return settings.customIconSizePx ?? ICON_SIZE_PX[settings.iconSize];
}
/**
 * Calculate icon position coordinates within a collapsed box
 */
export function getIconPositionCoords(boxWidth, boxHeight, iconSize, position) {
    const padding = 8;
    // Map position string to x coordinate
    let x;
    if (position.includes('left')) {
        x = padding;
    }
    else if (position.includes('right')) {
        x = boxWidth - iconSize - padding;
    }
    else {
        x = (boxWidth - iconSize) / 2;
    }
    // Map position string to y coordinate
    let y;
    if (position.includes('top')) {
        y = padding;
    }
    else if (position.includes('bottom')) {
        y = boxHeight - iconSize - padding;
    }
    else {
        y = (boxHeight - iconSize) / 2;
    }
    return { x, y };
}
//# sourceMappingURL=component-layout.types.js.map